import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import { useRefetchQuery } from "libs/reactQuery";
import {
  bannerValidationSchema,
  useCreateSpecialOffer,
  useUpdateSpecialOffer,
  useOffer,
  requestBodyData
} from "modules/offers";
import { ProductsFieldForForms } from "modules/products";
import { convertProductSetDetailsToListItem } from "modules/productSets";
import moment from "moment";
import { Controller, FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { Modal } from "ui";

import { MainInformation, PostingDateRange } from "./atoms";
import {
  CreateOfferModalProps,
  OfferFormValues
} from "./CreateOfferModal.types";

import styles from "./CreateOfferModal.module.scss";

const NEW_OFFER_FORM_ID = "NEW_OFFER_FORM_ID";

const CreateOfferModal = ({
  isOpen,
  onClose,
  offerId
}: CreateOfferModalProps) => {
  const refetchOffer = useRefetchQuery(["special-offer", `${offerId}`]);
  const refetchOffers = useRefetchQuery(["special-offers"]);

  const { offer } = useOffer(offerId);

  const {
    image = null,
    title = "",
    from = new Date(),
    to = new Date(),
    productVariants = [],
    productSet
  } = offer ?? {};

  const defaultValues: OfferFormValues = {
    image,
    title,
    fromPostingDate: new Date(from),
    fromPostingTime: moment(from).format("HH:mm"),
    toPostingDate: new Date(to),
    toPostingTime: moment(to).format("HH:mm"),
    productVariants,
    productSet: convertProductSetDetailsToListItem(productSet)
  };

  const { control, watch, register, handleSubmit, formState, ...methods } =
    useForm<OfferFormValues>({
      ...(offerId ? { values: defaultValues } : { defaultValues }),
      resolver: yupResolver(bannerValidationSchema)
    });

  const { mutate: mutateCreateOffer, isLoading: isLoadingCreateOffer } =
    useCreateSpecialOffer({
      onSuccess: () => {
        refetchOffers();
        onClose();
        methods.reset();
        toast.success(t("Special Offer was created successfully"));
      }
    });

  const { mutate: mutateUpdateOffer, isLoading: isLoadingUpdateOffer } =
    useUpdateSpecialOffer({
      onSuccess: () => {
        refetchOffers();
        refetchOffer();
        onClose();
        methods.reset();
        toast.success(t("Special Offer was updated successfully"));
      }
    });

  const onSubmit = (formData: OfferFormValues) => {
    const body = requestBodyData(formData, offerId);

    if (offerId) {
      mutateUpdateOffer({ id: offerId, body });
    } else {
      mutateCreateOffer(body);
    }
  };

  return (
    <FormProvider
      {...{
        ...methods,
        control,
        watch,
        register,
        handleSubmit,
        formState
      }}
    >
      <Modal
        formId={NEW_OFFER_FORM_ID}
        isOpen={isOpen}
        title={`${offerId ? "Edit" : "New"} Offer`}
        okText={offerId ? "Save" : "Create"}
        onClose={onClose}
        isLoading={offerId ? isLoadingCreateOffer : isLoadingUpdateOffer}
        onOk={handleSubmit(onSubmit)}
        className={styles.offer__modal}
        contentClassName={styles["offer__modal--content"]}
      >
        <MainInformation bannerType="Offer" />
        <PostingDateRange />

        <Controller
          control={control}
          name={"productVariants"}
          render={({
            field: { value: productsList, onChange: onChangeProductsList },
            fieldState: { error: productsListError }
          }) => (
            <Controller
              control={control}
              name={"productSet"}
              render={({
                field: { value: productSetField, onChange: onChangeProductSet }
              }) => (
                <ProductsFieldForForms
                  productsList={productsList}
                  onChangeProductsList={onChangeProductsList}
                  productSet={productSetField}
                  errorMessage={productsListError?.message}
                  onChangeProductSet={onChangeProductSet}
                />
              )}
            />
          )}
        />
      </Modal>
    </FormProvider>
  );
};

export default CreateOfferModal;
